import React, { useState } from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCreditCard,
  faMoneyBillWave,
  faStore,
  faTruck,
  faCalendarAlt,
  faClock,
  faMapMarkerAlt,
  faUser,
  faPhone,
  faEnvelope,
  faChevronDown,
  faShoppingBasket,
} from "@fortawesome/free-solid-svg-icons";
import { setPaymentMethod, setCheckoutStep } from "../../../redux/checkout/action";
import ReviewModal from "./ReviewModal";
import StripeEmbeddedCheckout from "./StripeEmbeddedCheckout";

const img_placeholder = require("../../../assets/images/prodotto.png");

function formatTime(t) {
  if (!t) return "";
  return t.substring(0, 5);
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("it-IT", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function PaymentStep(props) {
  const { checkout, groceryList } = props;
  const {
    paymentMethod,
    paymentMethods = [],
    deliveryMethod,
    shippingData,
    shippingCost,
    selectedStore,
    pickupDate,
    selectedSlot,
  } = checkout;

  const selectedMethod = paymentMethods.find((m) => m.key === paymentMethod);
  // Carta + provider Stripe -> embed inline; carta + Nexi -> redirect al gateway.
  const needsEmbed =
    paymentMethod === "card" && selectedMethod && selectedMethod.provider === "stripe";

  const [reviewOpen, setReviewOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleMethodChange = (method) => {
    props.setPaymentMethod(method);
  };

  const handleBack = () => {
    props.setCheckoutStep(2);
  };

  const handleConfirm = () => {
    setReviewOpen(true);
  };

  const cashLabel =
    deliveryMethod === "delivery" ? "alla consegna" : "al ritiro";

  // Calculate totals
  const subtotal = groceryList.list
    .filter((item) => !item.no_price)
    .reduce((sum, item) => sum + parseFloat(item.currentPrice || 0), 0);

  const shippingValue = deliveryMethod === "pickup" ? 0 : parseFloat(shippingCost || 0);
  const total = subtotal + shippingValue;
  const itemCount = groceryList.list.reduce((sum, item) => sum + (item.quantity || 1), 0);

  const slotLabel = selectedSlot
    ? formatTime(selectedSlot.time_from) + " - " + formatTime(selectedSlot.time_to)
    : "-";

  return (
    <div>
      {/* Payment method card */}
      <div className="co-card">
        <div className="co-card__header">
          <h2 className="co-card__title">Metodo di pagamento</h2>
        </div>

        <div className="co-form__group">
          <div className="co-form__radio">
            {paymentMethods.map((m) => (
              <div
                key={m.key}
                className={
                  "co-form__radio-item" +
                  (paymentMethod === m.key ? " co-form__radio-item--selected" : "")
                }
                onClick={() => handleMethodChange(m.key)}
              >
                <span className={"co-radio-dot" + (paymentMethod === m.key ? " co-radio-dot--active" : "")} />
                <FontAwesomeIcon icon={m.key === "cash" ? faMoneyBillWave : faCreditCard} />
                {m.key === "cash" ? `${m.label} ${cashLabel}` : m.label}
              </div>
            ))}
          </div>
        </div>

        {/* Carta + Stripe: embed inline */}
        {needsEmbed && (
          <div style={{ marginTop: 14 }}>
            <StripeEmbeddedCheckout />
          </div>
        )}

        {/* Carta + Nexi: si paga tramite redirect al gateway (alla conferma) */}
        {paymentMethod === "card" && selectedMethod && selectedMethod.provider === "nexi" && (
          <div style={{
            marginTop: 14,
            padding: "10px 12px",
            backgroundColor: "#f0f7ff",
            borderRadius: 8,
            fontSize: "0.82rem",
            color: "#4a6fa5",
            lineHeight: 1.5,
          }}>
            Al momento della conferma verrai reindirizzato al gateway di pagamento sicuro.
          </div>
        )}
      </div>

      {/* Delivery recap card */}
      <div className="co-card" style={{ marginTop: 16 }}>
        <div className="co-card__header">
          <h2 className="co-card__title">Riepilogo consegna</h2>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Delivery method */}
          <div className="co-recap__row">
            <FontAwesomeIcon
              icon={deliveryMethod === "pickup" ? faStore : faTruck}
              className="co-recap__icon"
            />
            <div>
              <div className="co-recap__label">Metodo</div>
              <div className="co-recap__value">
                {deliveryMethod === "pickup" ? "Ritiro in negozio" : "Consegna a domicilio"}
              </div>
            </div>
          </div>

          {/* Pickup details */}
          {deliveryMethod === "pickup" && (
            <>
              <div className="co-recap__row">
                <FontAwesomeIcon icon={faStore} className="co-recap__icon" />
                <div>
                  <div className="co-recap__label">Punto vendita</div>
                  <div className="co-recap__value">
                    {selectedStore?.name || selectedStore?.nome || "-"}
                  </div>
                  <div className="co-recap__sub">
                    {selectedStore?.address || selectedStore?.indirizzo || ""}
                  </div>
                </div>
              </div>
              <div className="co-recap__row">
                <FontAwesomeIcon icon={faCalendarAlt} className="co-recap__icon" />
                <div>
                  <div className="co-recap__label">Data ritiro</div>
                  <div className="co-recap__value">{formatDate(pickupDate)}</div>
                </div>
              </div>
              <div className="co-recap__row">
                <FontAwesomeIcon icon={faClock} className="co-recap__icon" />
                <div>
                  <div className="co-recap__label">Orario ritiro</div>
                  <div className="co-recap__value">{slotLabel}</div>
                </div>
              </div>
            </>
          )}

          {/* Delivery details */}
          {deliveryMethod === "delivery" && (
            <div className="co-recap__row">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="co-recap__icon" />
              <div>
                <div className="co-recap__label">Indirizzo di consegna</div>
                <div className="co-recap__value">{shippingData.indirizzo}</div>
                <div className="co-recap__sub">
                  {shippingData.cap} {shippingData.citta}
                </div>
                {shippingData.note && (
                  <div className="co-recap__sub" style={{ fontStyle: "italic" }}>
                    Note: {shippingData.note}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Personal data */}
          <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 12, marginTop: 4 }}>
            <div className="co-recap__row">
              <FontAwesomeIcon icon={faUser} className="co-recap__icon" />
              <div>
                <div className="co-recap__label">Intestatario</div>
                <div className="co-recap__value">
                  {shippingData.nome} {shippingData.cognome}
                </div>
              </div>
            </div>
            <div className="co-recap__row" style={{ marginTop: 8 }}>
              <FontAwesomeIcon icon={faEnvelope} className="co-recap__icon" />
              <div className="co-recap__value" style={{ fontSize: "0.88rem" }}>{shippingData.email}</div>
            </div>
            <div className="co-recap__row" style={{ marginTop: 8 }}>
              <FontAwesomeIcon icon={faPhone} className="co-recap__icon" />
              <div className="co-recap__value" style={{ fontSize: "0.88rem" }}>{shippingData.telefono}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Order totals card */}
      <div className="co-card" style={{ marginTop: 16 }}>
        <div className="co-card__header">
          <h2 className="co-card__title">Riepilogo ordine</h2>
        </div>

        {/* Product preview accordion */}
        <div className={"co-preview" + (previewOpen ? " co-preview--open" : "")}>
          <button
            type="button"
            className="co-preview__toggle"
            onClick={() => setPreviewOpen((v) => !v)}
            aria-expanded={previewOpen}
          >
            <span className="co-preview__toggle-label">
              <FontAwesomeIcon icon={faShoppingBasket} className="co-preview__toggle-icon" />
              {previewOpen ? "Nascondi prodotti" : "Mostra prodotti"} ({itemCount})
            </span>
            <FontAwesomeIcon icon={faChevronDown} className="co-preview__chevron" />
          </button>

          {previewOpen && (
            <div className="co-preview__list">
              {groceryList.list.map((item, idx) => {
            const imageSrc =
              item.images && item.images.length > 0 && item.images[0].image_file
                ? item.images[0].image_file
                : img_placeholder;
            const lineTotal = item.no_price
              ? null
              : parseFloat(item.currentPrice || 0).toFixed(2);
            return (
              <div
                className="co-preview__item"
                key={item.item_id + "-" + (item.variety || idx)}
              >
                <div className="co-preview__img">
                  <img
                    src={imageSrc}
                    alt={item.field1 || ""}
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null; // previene il loop
                      currentTarget.src = img_placeholder;
                    }}
                  />
                  <span className="co-preview__qty">{item.quantity}</span>
                </div>
                <div className="co-preview__info">
                  <p className="co-preview__name">
                    {item.field1 || item.description || "Prodotto"}
                  </p>
                  {item.variety && (
                    <span className="co-preview__variety">{item.variety}</span>
                  )}
                </div>
                <span className="co-preview__price">
                  {lineTotal !== null ? "€ " + lineTotal : ""}
                </span>
              </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="co-summary">
          <div className="co-summary__row">
            <span>Subtotale ({itemCount} prodotti)</span>
            <span>€ {subtotal.toFixed(2)}</span>
          </div>
          {deliveryMethod === "delivery" && (
            <div className="co-summary__row">
              <span>Spedizione</span>
              <span style={{ color: shippingValue === 0 ? "#28a745" : "#222" }}>
                {shippingValue === 0 ? "Gratuita" : "€ " + shippingValue.toFixed(2)}
              </span>
            </div>
          )}
          <div className="co-summary__row co-summary__total">
            <span>Totale</span>
            <span>€ {total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Navigation buttons */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
        <button className="up-btn up-btn--ghost" onClick={handleBack}>
          Indietro
        </button>
        {paymentMethod && !needsEmbed && (
          <button
            className="up-btn up-btn--primary"
            onClick={handleConfirm}
          >
            Conferma ordine
          </button>
        )}
      </div>

      <ReviewModal
        isOpen={reviewOpen}
        toggle={() => setReviewOpen(false)}
        onConfirm={() => setReviewOpen(false)}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  checkout: state.checkout,
  groceryList: state.groceryList,
});

export default connect(mapStateToProps, {
  setPaymentMethod,
  setCheckoutStep,
})(PaymentStep);
